/**
 * ShadowEditor — Enable/disable checkbox with shadow controls below
 *
 * UX pattern matches GradientEditor: single checkbox toggles the entire
 * shadow feature ON/OFF, with color/blur/offsetY fields shown when enabled.
 *
 * Data: reads/writes `appearance.shadow` as a ShadowStyle object.
 *
 * TestId convention: `style-group-shadow-{control}`
 */

import React, { useCallback, useMemo } from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import type { ShadowStyle } from '@segmently/schema-models/v2';
import { ColorInput } from './ColorInput';
import { StyledNumberInput } from '@modules/ui-components';
import {
  checkboxIconSx,
  checkboxCheckedIconSx,
  checkboxSx,
  fieldLabelSx,
  STYLE_FIELD_LABEL_HEIGHT,
} from '../../../config/editorFieldStyles';
import { FIELD_GAP_SMALL } from '../../../config/ui';

const DEFAULT_SHADOW: ShadowStyle = {
  color: '#00000040',
  blur: 4,
  offsetY: 2,
};

export interface ShadowEditorProps {
  value: ShadowStyle | null | undefined;
  onChange: (shadow: ShadowStyle | null) => void;
  disabled?: boolean;
}

export const ShadowEditor: React.FC<ShadowEditorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const isEnabled = value != null;
  const shadow = useMemo(() => value ?? DEFAULT_SHADOW, [value]);

  const handleToggle = useCallback(
    (_: unknown, checked: boolean) => {
      onChange(checked ? { ...DEFAULT_SHADOW } : null);
    },
    [onChange],
  );

  const updateField = useCallback(
    <K extends keyof ShadowStyle>(field: K, val: ShadowStyle[K]) => {
      onChange({ ...shadow, [field]: val });
    },
    [shadow, onChange],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }} data-testid="style-group-shadow">
      {/* Enable toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP_SMALL, height: STYLE_FIELD_LABEL_HEIGHT, minHeight: STYLE_FIELD_LABEL_HEIGHT }}>
        <Checkbox
          size="small"
          checked={isEnabled}
          onChange={handleToggle}
          disabled={disabled}
          icon={<Box sx={checkboxIconSx} />}
          checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
          sx={checkboxSx}
          data-testid="style-group-shadow-enable"
        />
        <Typography variant="caption" color="text.secondary" sx={fieldLabelSx}>
          Enable shadow
        </Typography>
      </Box>

      {isEnabled && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Color */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
              Color
            </Typography>
            <ColorInput
              value={shadow.color ?? '#00000040'}
              onChange={(c) => updateField('color', c as string)}
              disabled={disabled}
              testId="style-group-shadow-color"
            />
          </Box>

          {/* Blur + OffsetY in 2-column grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                Blur
              </Typography>
              <StyledNumberInput
                value={shadow.blur ?? 4}
                onChange={(val) => updateField('blur', val ?? 4)}
                min={0}
                max={50}
                disabled={disabled}
                data-testid="style-group-shadow-blur"
              />
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
                Offset Y
              </Typography>
              <StyledNumberInput
                value={shadow.offsetY ?? 2}
                onChange={(val) => updateField('offsetY', val ?? 2)}
                min={-50}
                max={50}
                disabled={disabled}
                data-testid="style-group-shadow-offset-y"
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
